import { CheckboxField } from '@sunwebgroup/uds-ui/fields/checkbox-field'
import { TextField } from '@sunwebgroup/uds-ui/fields/text-field'
import cn from 'classnames'
import * as React from 'react'
import { ServiceItem } from '@/cms/fields/types/checkout-page'
import { CheckoutSection } from '@/scopes/checkout/components/CheckoutSection'
import { updatePreferenceSelection } from '@/scopes/checkout/domain/actions/preferencesFormData'
import { createPreferenceSelectionEvent } from '@/scopes/checkout/domain/analytics'
import { usePreferences } from '@/scopes/checkout/domain/hooks'
import { APIBookingDraftPreferenceType } from '@/scopes/checkout/infrastructure/api/types'
import { PREFERENCE_CUSTOMER_INPUT_MAX_LENGTH } from '@/scopes/common/domain/policies/bookingPreference'
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'
import styles from './Preferences.module.css'

interface PreferencesProps {
  item: ServiceItem
}

export function Preferences({
  item,
}: PreferencesProps) {
  const { componentTitle, componentDescription, popupButtonAriaLabel, popupTitle, popupDescription, popupReadMoreLabel } = item
  const { sendEvent } = useAnalytics()
  const { preferences: availablePreferences, setPreferencesSelectionValue } = usePreferences()

  const handlePreferenceChange = React.useCallback((id: string, checked: boolean) => {
    if (!id)
      return
    setPreferencesSelectionValue(updatePreferenceSelection({ checked }), id)
    sendEvent(
      createPreferenceSelectionEvent({
        type: availablePreferences?.[id]?.text ?? id,
        result: checked ? 'preference selected' : 'preference deselected',
      }),
    )
  }, [availablePreferences, sendEvent, setPreferencesSelectionValue])

  if (!availablePreferences || Object.keys(availablePreferences).length === 0) {
    return null
  }

  return (
    <CheckoutSection
      title={componentTitle ?? ''}
      description={componentDescription ?? ''}
      glyph="Bed"
      moreInformationTitle={popupTitle ?? ''}
      moreInformationDescription={popupDescription}
      moreInformationButtonAriaLabel={popupButtonAriaLabel}
      moreInformationReadMoreLabel={popupReadMoreLabel}
    >
      <div className={styles.checkoutSectionColumnView}>
        {Object.values(availablePreferences).map(preference => (
          <PreferenceComponent
            label={preference.text}
            id={preference.id}
            key={preference.id}
            checked={preference.selected ?? false}
            type={preference.type}
            value={preference.value}
            onCheckedChange={(checked: boolean | string) => handlePreferenceChange(preference.id, Boolean(checked))}
          />
        ))}
      </div>
    </CheckoutSection>
  )
}

function PreferenceComponent({
  label,
  id,
  checked,
  onCheckedChange,
  type,
  value,
}: {
  label: string
  id: string
  checked: boolean
  onCheckedChange: (checked: boolean | string) => void
  type: APIBookingDraftPreferenceType
  value: string | null
}) {
  const Component: Partial<Record<APIBookingDraftPreferenceType, React.ReactNode>> = {
    Standard: (
      <CheckboxField
        label={label}
        name={id}
        checked={checked ?? false}
        onCheckedChange={onCheckedChange}
        id={id}
      />
    ),
    CustomerInput: (
      <CustomerInputPreference
        label={label}
        checked={checked ?? false}
        onCheckedChange={onCheckedChange}
        id={id}
        defaultTextValue={value !== id ? value : null}
      />
    ),
  }

  return Component[type]
}

function CustomerInputPreference({
  label,
  id,
  checked,
  onCheckedChange,
  defaultTextValue,
}: {
  label: string
  id: string
  checked: boolean
  onCheckedChange: (checked: boolean | string) => void
  defaultTextValue: string | null
}) {
  const [isTextInputVisible, setIsTextInputVisible] = React.useState(checked)
  const [inputValue, setInputValue] = React.useState(defaultTextValue ?? '')
  const { setPreferencesSelectionValue } = usePreferences()

  const handleCheckboxChange = (newChecked: boolean | string) => {
    setIsTextInputVisible(Boolean(newChecked))
    onCheckedChange(newChecked)
  }

  const handleInputPreferenceChange = React.useCallback((id: string, checked: boolean, inputValue?: string) => {
    if (!id)
      return
    setPreferencesSelectionValue(updatePreferenceSelection({ checked, value: inputValue }), id)
  }, [setPreferencesSelectionValue])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value
    setInputValue(newValue)
    handleInputPreferenceChange(id, checked, newValue)
  }

  const handleInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const newValue = e.target.value
    handleInputPreferenceChange(id, checked, newValue)
  }

  const customInputStyles = cn(styles.customerInputVisible, {
    [styles.customerInputHidden]: !isTextInputVisible,
  })

  return (
    <div className={styles.customerInputContainer}>
      <CheckboxField
        label={label}
        name={id}
        checked={checked ?? false}
        onCheckedChange={handleCheckboxChange}
        id={id}
      />
      <TextField
        id={`${id}-input`}
        value={inputValue}
        onChange={handleInputChange}
        onBlur={handleInputBlur}
        inputProps={{ maxLength: PREFERENCE_CUSTOMER_INPUT_MAX_LENGTH }}
        className={customInputStyles}
      />
    </div>
  )
}
